import { type FC } from 'react';
import { type EnergyAudit } from 'interfaces/EnergyAudit.interface';
import RenderBlocks from '../lists/BulletBlockList';
import SourceLinksList from 'components/links/SourceLinksList';

interface EnergyAuditRiskComplianceTabProps {
  data: EnergyAudit;
}

function renderSection(
  title: string,
  section: any,
  emptyMessage: string,
  urls?: string[]
): React.ReactNode {
  const hasUrls = Array.isArray(urls) && urls.length > 0;
  if (
    section &&
    typeof section === 'object' &&
    'blocks' in section &&
    Array.isArray(section.blocks)
  ) {
    const blocks = section.blocks;
    return (
      <>
        <RenderBlocks blocks={blocks} emptyMessage={emptyMessage} />
        {hasUrls && <SourceLinksList urlList={urls} />}
      </>
    );
  }
  // Handle array of answers (e.g. [{ answer: "..." }])
  if (
    Array.isArray(section) &&
    section.length > 0 &&
    section.every((item) => typeof item === 'object' && 'answer' in item)
  ) {
    return (
      <div className="mb-2 whitespace-pre-line flex flex-col gap-2">
        {section.map((item, idx) => (
          <div key={idx}>{item.answer}</div>
        ))}
        {hasUrls && <SourceLinksList urlList={urls} />}
      </div>
    );
  }
  if (typeof section === 'string' && section.trim() !== '') {
    return (
      <div className="mb-2 whitespace-pre-line">
        {section}
        {hasUrls && <SourceLinksList urlList={urls} />}
      </div>
    );
  }
  return <div className="mb-4">{emptyMessage}</div>;
}

const EnergyAuditRiskComplianceTab: FC<EnergyAuditRiskComplianceTabProps> = ({
  data,
}) => {
  const riskBlocks =
    data.risks &&
    typeof data.risks === 'object' &&
    'blocks' in data.risks &&
    Array.isArray((data.risks as any).blocks)
      ? (data.risks as any).blocks
      : [];

  return (
    <div className="text-gray-60 text-sm leading-[21px] font-sans">
      <div className="mb-6">
        <div className="font-bold text-base mb-2">Risks</div>
        <RenderBlocks
          blocks={riskBlocks}
          emptyMessage="No risk content available."
        />
      </div>
      <div className="mb-6">
        <div className="font-bold text-base mb-2">Legal Compliance</div>
        {renderSection(
          'Legal Compliance',
          data.legal_compliance,
          'No legal compliance content available.',
          data.legal_compliance_url
        )}
      </div>
      <div>
        <div className="font-bold text-base mb-2">Technical Compliance</div>
        {renderSection(
          'Technical Compliance',
          data.technical_compliance,
          'No technical compliance content available.'
        )}
      </div>
    </div>
  );
};

export default EnergyAuditRiskComplianceTab;
